// Ubah paket (the PBJ project home, pbj/PbjProject.tsx): the paket data of a
// Non-Penyedia PBJ project through PATCH /pbj/projects/:id. A new route,
// method or Swakelola type recomputes the activities on the API; ticks of
// activities still on the new route stay, the others go, and the dialog
// says so before saving.
import { useEffect, useId, useState, type FormEvent } from "react";
import * as API from "../api";
import type { PbjProject, PbjProjectDetail } from "../api";
import { Dialog, Notice } from "../ui/components";
import { PaketFields, paketFormOf, paketInput, tahunError, type PaketForm } from "./PaketFields";

export interface PaketDialogProps {
  open: boolean;
  project: PbjProject;
  onSaved: (next: PbjProjectDetail) => void;
  onClose: () => void;
}

/** True when the form's route differs from the stored one (the activities change). */
export function reroutes(form: PaketForm, p: PbjProject): boolean {
  if (form.route !== p.route) return true;
  return form.route === "penyedia" ? form.method !== p.method : form.swakelolaType !== p.swakelolaType;
}

export function PaketDialog({ open, project, onSaved, onClose }: PaketDialogProps) {
  const formId = `${useId()}-form`;
  const [form, setForm] = useState<PaketForm>(() => paketFormOf(project));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Every opening starts from the stored values.
  useEffect(() => {
    if (!open) return;
    setForm(paketFormOf(project));
    setError(null);
  }, [open, project]);

  const changesRoute = reroutes(form, project);
  const ready = !busy && tahunError(form) === null;

  const submit = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!ready) return;
    setBusy(true);
    setError(null);
    try {
      onSaved(await API.updatePbjProject(project.id, paketInput(form)));
    } catch (err) {
      setError(API.errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      title="Ubah paket"
      size="sm"
      busy={busy}
      onClose={onClose}
      footer={
        <button type="submit" form={formId} className="btn btn-primary" disabled={!ready} aria-busy={busy || undefined}>
          Simpan
        </button>
      }
    >
      <form id={formId} className="pj-form pbj-create" onSubmit={(e) => void submit(e)} noValidate>
        <PaketFields
          value={form}
          onChange={setForm}
          disabled={busy}
          routeHint={changesRoute ? "Aktivitas disusun ulang sesuai jalur baru. Centang pada aktivitas yang tetap ada tidak hilang." : undefined}
        />
        {error ? (
          <Notice tone="danger" title="Paket belum tersimpan">
            <p>{error}</p>
          </Notice>
        ) : null}
      </form>
    </Dialog>
  );
}
